import { Link } from '@tanstack/react-router';
import {
  IconSchool,
  IconShieldCheck,
  IconFlag,
  IconMapPin,
  IconCalendar,
  IconStar,
  IconStarFilled,
  IconGitCompare,
  IconCheck,
  IconCertificate,
} from '@tabler/icons-react';
import { AQF_LEVEL_LABELS, type CourseSummary } from '@admitly/shared';
import { centsToAud, formatIntakes } from '../../lib/format.js';
import { cn } from '../../lib/cn.js';

type Props = {
  course: CourseSummary;
  /** Star (shortlist) action — rendered only when provided. */
  isStarred?: boolean;
  onToggleStar?: (courseId: string) => void;
  /** Compare-select control — rendered only when compareSelectable is true. */
  compareSelectable?: boolean;
  compareSelected?: boolean;
  onToggleCompare?: (courseId: string) => void;
};

export function CourseCard({
  course,
  isStarred,
  onToggleStar,
  compareSelectable,
  compareSelected,
  onToggleCompare,
}: Props) {
  // The badge means a person checked this course — only the recorded
  // verification stamp can show it (never curation state).
  const isVerified = course.verifiedAt != null;

  // The card is a Link; action controls must not trigger navigation.
  const handleStar = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleStar?.(course.id);
  };
  const handleCompare = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleCompare?.(course.id);
  };

  return (
    <Link
      to="/app/courses/$category/$subCategory/$courseId"
      params={{
        category: course.category,
        subCategory: course.subCategory,
        courseId: course.id,
      }}
      className="group block surface p-5 hover:border-navy/30 hover:shadow-pop hover:-translate-y-0.5 transition-all duration-200"
    >
      {/* Provider tag + badges row */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="inline-flex items-center gap-1.5 rounded-md bg-cream-deep/70 px-2 py-1">
          <IconSchool size={14} className="text-gold" />
          <span className="text-xs font-medium text-navy tracking-tightish">
            {course.providerShortName ?? course.providerName}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {course.isPreferred && (
            <span className="badge bg-gold/15 text-navy inline-flex items-center gap-1">
              <IconStarFilled size={11} />
              Preferred
            </span>
          )}
          {course.prRelevant && (
            <span className="badge bg-warn-bg text-warn inline-flex items-center gap-1">
              <IconFlag size={11} />
              PR pathway
            </span>
          )}
          {isVerified && (
            <span className="badge bg-success-bg text-success inline-flex items-center gap-1">
              <IconShieldCheck size={11} />
              Verified
            </span>
          )}
          {onToggleStar && (
            <button
              type="button"
              onClick={handleStar}
              aria-pressed={isStarred}
              aria-label={isStarred ? 'Remove from shortlist' : 'Add to shortlist'}
              className={cn(
                'flex h-11 w-11 items-center justify-center -my-2 -mr-2 rounded-md transition sm:h-auto sm:w-auto sm:my-0 sm:mr-0 sm:p-1',
                isStarred
                  ? 'text-gold'
                  : 'text-muted-soft hover:text-gold hover:bg-cream-deep/60',
              )}
            >
              {isStarred ? <IconStarFilled size={16} /> : <IconStar size={16} />}
            </button>
          )}
        </div>
      </div>

      {/* Title */}
      <h3 className="font-serif text-lg leading-snug tracking-tightish text-ink mb-1 group-hover:text-navy transition-colors">
        {course.name}
      </h3>

      {/* CRICOS */}
      {course.cricosCode && (
        <div className="text-[11px] font-mono text-muted-soft tracking-wide mb-4">
          CRICOS {course.cricosCode}
        </div>
      )}

      {/* Meta pills */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted">
        {course.aqfLevel && (
          <span
            className="inline-flex items-center gap-1"
            title={AQF_LEVEL_LABELS[course.aqfLevel]}
          >
            <IconCertificate size={12} />
            {AQF_LEVEL_LABELS[course.aqfLevel].split(' — ')[0]}
          </span>
        )}
        {course.campusLocations[0] && (
          <span className="inline-flex items-center gap-1">
            <IconMapPin size={12} />
            {course.campusLocations.join(', ')}
          </span>
        )}
        {course.durationText && (
          <span className="inline-flex items-center gap-1">
            <IconCalendar size={12} />
            {course.durationText}
          </span>
        )}
        {course.intakeMonths.length > 0 && (
          <span className="inline-flex items-center gap-1">
            {formatIntakes(course.intakeMonths)}
          </span>
        )}
      </div>

      {/* Footer: fee */}
      <div className="mt-4 pt-3 border-t border-line flex items-baseline justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.12em] text-muted-soft font-semibold">
            Annual fee
          </div>
          <div className="font-serif text-xl text-ink tracking-tightish">
            {centsToAud(course.annualTuitionCents)}
          </div>
        </div>
        {course.ieltsOverall && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.12em] text-muted-soft font-semibold">
              IELTS
            </div>
            <div className="text-sm font-medium text-ink">{course.ieltsOverall}</div>
          </div>
        )}
      </div>

      {/* Compare-select (shortlist page only) */}
      {compareSelectable && (
        <button
          type="button"
          onClick={handleCompare}
          aria-pressed={compareSelected}
          className={cn(
            'mt-3 inline-flex items-center gap-1.5 text-xs font-medium transition',
            compareSelected ? 'text-navy' : 'text-muted hover:text-navy',
          )}
        >
          <span
            className={cn(
              'h-4 w-4 rounded border flex items-center justify-center',
              compareSelected ? 'bg-navy border-navy text-cream' : 'border-line',
            )}
          >
            {compareSelected && <IconCheck size={11} />}
          </span>
          <IconGitCompare size={13} />
          Compare
        </button>
      )}
    </Link>
  );
}
